import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { sectionScheme } from "@/lib/schemes";
import { useSiteContent } from "@/context/SiteContentContext";
import Seo from "@/components/Seo";

/**
 * Product detail — real page (replaces the old QuickView modal).
 * Preserves original photography, buy → Etsy, related products at the bottom.
 */
export default function ProductDetail() {
    const { t, currency: globalCurrency, setCurrency: setGlobalCurrency } = useT();
    const { id } = useParams();
    const { content: siteContent } = useSiteContent();
    const [product, setProduct] = useState(null);
    const [allProducts, setAllProducts] = useState([]);
    const [mainImg, setMainImg] = useState("");
    // Currency lives in the language context (EN→USD, ES→MXN) so the pair
    // stays consistent site-wide. The toggle below still lets the user override.
    const displayCurrency = globalCurrency;
    const setDisplayCurrency = setGlobalCurrency;
    const fxUsdToMxn = parseFloat(siteContent.fx_usd_to_mxn || "20") || 20;

    useEffect(() => {
        api.get("/products").then((r) => {
            const list = Array.isArray(r.data) ? r.data : [];
            setAllProducts(list);
            const found = list.find((p) => p.id === id);
            setProduct(found || null);
            setMainImg(found?.image_url || (found?.gallery_urls || [])[0] || "");
        });
    }, [id]);

    const related = useMemo(() => {
        if (!product) return [];
        const list = allProducts.filter((x) => x.id !== product.id);
        const scored = list.map((x) => {
            let score = 0;
            const oa = new Set([x.artist_slug, ...(x.artist_slugs || [])].filter(Boolean));
            const ma = new Set([product.artist_slug, ...(product.artist_slugs || [])].filter(Boolean));
            for (const s of oa) if (ma.has(s)) score += 3;
            const oc = new Set(x.collection_slugs || []);
            const mc = new Set(product.collection_slugs || []);
            for (const s of oc) if (mc.has(s)) score += 2;
            if (x.product_type && x.product_type === product.product_type) score += 1;
            return { x, score };
        }).filter((r) => r.score > 0).sort((a, b) => b.score - a.score);
        return scored.slice(0, 4).map((r) => r.x);
    }, [allProducts, product]);

    if (product === null && allProducts.length === 0) {
        return <div className="py-40 text-center font-mono text-xs uppercase tracking-[0.35em] opacity-50">loading…</div>;
    }
    if (!product) {
        return (
            <div className="py-40 text-center">
                <div className="font-mono text-xs uppercase tracking-[0.35em] opacity-50 mb-4">product not found</div>
                <Link to="/shop" className="font-mono text-[10px] uppercase tracking-[0.3em] underline decoration-1 underline-offset-4">← back to shop</Link>
            </div>
        );
    }

    const p = product;
    const cur = p.currency === "EUR" ? "€" : "$";
    // Format a price for display — strip trailing zeros so "$800.00" → "$800",
    // "$499.95" stays. Keep 2 decimals when there ARE cents. Etsy inventory
    // rarely uses fractional cents so 2-digit trim is enough.
    function fmt(v) {
        const n = Number(v);
        if (!isFinite(n)) return "0";
        // .toFixed(2) then strip trailing zeros + dangling dot
        return n.toFixed(2).replace(/\.?0+$/, "");
    }

    const price = Number(p.price) || 0;
    const finalPrice = p.sale_percent > 0 ? price * (1 - p.sale_percent / 100) : price;
    const gallery = [p.image_url, p.image_url_alt, ...(p.gallery_urls || [])].filter(Boolean);
    const buyUrl = p.etsy_url || p.shopify_url; // shopify still supported behind the scenes
    const buyLabel = "buy on etsy"; // Always "etsy" per current inventory location

    // The product detail page inherits the /shop page scheme from Layout —
    // no per-product override so the whole store feels cohesive with what the
    // admin picked for the shop. If we ever want per-product rooms again,
    // add a per-tile scheme wrapper INSIDE this container instead of theming
    // the whole page.

    // Admin controls (site_content):
    //   product_main_image_pct   — 30..100 (percent of the image column width)
    //   product_text_align       — left | center | right (info column)
    const mainImagePct = Math.max(30, Math.min(100, parseInt(siteContent.product_main_image_pct || "50", 10) || 50));
    const textAlign = (siteContent.product_text_align || "left").toLowerCase();
    const alignCls = textAlign === "center" ? "text-center items-center" : textAlign === "right" ? "text-right items-end" : "text-left items-start";

    // Currency conversion — the product stores its own base currency. Convert
    // to display currency using the admin FX rate.
    const baseCurrency = p.currency === "MXN" ? "MXN" : (p.currency || "USD");
    function convert(v) {
        if (baseCurrency === displayCurrency) return v;
        if (baseCurrency === "USD" && displayCurrency === "MXN") return v * fxUsdToMxn;
        if (baseCurrency === "MXN" && displayCurrency === "USD") return v / fxUsdToMxn;
        return v;
    }
    const curSymbol = displayCurrency === "MXN" ? "$" : (displayCurrency === "EUR" ? "€" : "$");
    const curSuffix = displayCurrency === "MXN" ? " MXN" : (displayCurrency === "USD" ? " USD" : "");

    return (
        <div data-testid="product-detail" className="px-4 md:px-8 py-8 md:py-14">
            {product && (
                <Seo
                    title={`${product.title} | ITZKATU shop`}
                    description={(product.description || product.title || "").slice(0, 160)}
                    image={product.image_url || ""}
                    path={`/shop/${product.id}`}
                />
            )}
            {/* max-w-4xl outer + w-fit inner mx-auto → the 3-column group
                sizes itself to its content and centers on the page with
                strictly EQUAL margin left and right, no matter the image %. */}
            <div className="max-w-4xl mx-auto">
                <div className="w-fit mx-auto flex flex-col md:flex-row md:items-start gap-6 md:gap-8">
                    {/* Thumbnails column — vertical strip */}
                    {gallery.length > 1 && (
                        <div className="flex md:flex-col gap-2 shrink-0 order-1" data-testid="product-thumbs">
                            {gallery.map((g, i) => (
                                <button
                                    key={i}
                                    onClick={() => setMainImg(g)}
                                    data-testid={`product-thumb-${i}`}
                                    className={`w-14 h-14 md:w-16 md:h-16 overflow-hidden bg-white transition-opacity ${mainImg === g ? "opacity-100 ring-1 ring-current" : "opacity-50 hover:opacity-90"}`}
                                >
                                    <img src={g} alt="" className="w-full h-full object-contain" referrerPolicy="no-referrer" />
                                </button>
                            ))}
                        </div>
                    )}
                    {/* Main image column — width computed to fixed pixels so the
                        3-column group has a deterministic total width and centers
                        cleanly (mainImagePct=50 → 400px, 100 → 800px). Prevents
                        the % from becoming circular inside a w-fit flex. */}
                    <div
                        className="shrink-0 order-2"
                        style={{ width: `${Math.round(mainImagePct * 8)}px` }}
                    >
                        <div className="aspect-square overflow-hidden bg-white flex items-center justify-center w-full">
                            {mainImg && (
                                <img
                                    src={mainImg}
                                    alt={p.title}
                                    className="max-w-full max-h-full w-auto h-auto object-contain"
                                    referrerPolicy="no-referrer"
                                />
                            )}
                        </div>
                    </div>

                    {/* Info column — fixed width, not flex-1, so the 3-column
                        group stays TRUE CENTER on the page (equal margin left
                        and right). Previously `flex-1` stretched to fill the
                        row and pushed the text off-center to the right. */}
                    <div className={`w-full md:w-[280px] shrink-0 flex flex-col gap-5 order-3 ${alignCls}`}>
                        <div>
                            <h1 className="font-heading font-black text-3xl md:text-5xl uppercase tracking-tight leading-[0.95]">
                                {p.title}
                            </h1>
                            {p.product_type && (
                                <div className="mt-2 font-body italic text-sm opacity-60">{p.product_type}</div>
                            )}
                            {p.artist_credit && (
                                <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.25em] opacity-70">
                                    {p.artist_slug ? (
                                        <Link to={`/artists/${p.artist_slug}`} className="hover:text-[color:var(--sc-accent)] underline decoration-1 underline-offset-4">
                                            {p.artist_credit}
                                        </Link>
                                    ) : p.artist_credit}
                                </div>
                            )}
                        </div>

                        {/* Currency toggle first — sits above the price so the
                            active currency is obvious before you read the number. */}
                        <div className="inline-flex border border-current rounded-sm overflow-hidden self-start" data-testid="currency-toggle">
                            {["USD", "MXN"].map((c, i) => (
                                <button
                                    key={c}
                                    onClick={() => setDisplayCurrency(c)}
                                    data-testid={`currency-${c.toLowerCase()}`}
                                    className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 transition-colors ${displayCurrency === c ? "bg-current" : "opacity-70 hover:opacity-100"} ${i === 0 ? "border-r border-current" : ""}`}
                                >
                                    <span className={displayCurrency === c ? "text-[color:var(--sc-bg)]" : ""}>{c}</span>
                                </button>
                            ))}
                        </div>

                        {/* Price — big, sits right below the currency toggle */}
                        <div className="font-mono text-2xl md:text-3xl">
                            {p.sale_percent > 0 && !p.sold_out ? (
                                <>
                                    <span className="text-[color:var(--sc-accent)]">{curSymbol}{fmt(convert(finalPrice))}{curSuffix}</span>{" "}
                                    <span className="opacity-40 line-through text-base">{curSymbol}{fmt(convert(price))}</span>
                                </>
                            ) : (
                                <span className={p.sold_out ? "opacity-40 line-through" : ""}>
                                    {curSymbol}{fmt(convert(price))}{curSuffix}
                                </span>
                            )}
                        </div>

                        {/* Buy CTA — right below price */}
                        <div>
                            {!p.sold_out && buyUrl ? (
                                <a
                                    href={buyUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="product-buy"
                                    className="inline-flex items-center gap-2 bg-[color:var(--sc-accent)] text-white px-5 py-3 font-mono text-[11px] uppercase tracking-[0.28em] hover:opacity-90 transition-opacity"
                                >
                                    {buyLabel} <ArrowUpRight size={12} />
                                </a>
                            ) : (
                                <span className="font-mono text-[11px] uppercase tracking-[0.28em] opacity-40">sold out</span>
                            )}
                        </div>

                        {/* Description — smaller than before per user request */}
                        {p.description && (
                            <p className="font-body text-xs md:text-sm leading-relaxed opacity-80 whitespace-pre-line">
                                {p.description}
                            </p>
                        )}

                        {/* Optional editorial sections — kept but smaller */}
                        {(p.story || p.about_maker || (p.materials && p.materials.length > 0) || p.care_instructions || p.notes) && (
                            <div className="mt-4 space-y-5 text-left w-full">
                                {p.story && <Section title="story">{p.story}</Section>}
                                {p.about_maker && <Section title="about the maker">{p.about_maker}</Section>}
                                {p.materials && p.materials.length > 0 && (
                                    <Section title="materials">{p.materials.join(" · ")}</Section>
                                )}
                                {p.care_instructions && <Section title="care">{p.care_instructions}</Section>}
                                {p.notes && <Section title="notes">{p.notes}</Section>}
                            </div>
                        )}
                    </div>
                </div>

                {/* Related */}
                {related.length > 0 && (
                    <div className="mt-24">
                        <div className="font-mono text-[10px] uppercase tracking-[0.35em] opacity-50 mb-8">also from this world</div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-12">
                            {related.map((r) => (
                                <Link
                                    key={r.id}
                                    to={`/shop/${r.id}`}
                                    data-testid={`product-related-${r.id}`}
                                    className="group flex flex-col items-center"
                                >
                                    <div className="w-full aspect-square bg-[#F5F5F5] overflow-hidden">
                                        {r.image_url && <img src={r.image_url} alt={r.title} className="w-full h-full object-cover group-hover:opacity-80 transition-opacity duration-500" referrerPolicy="no-referrer" />}
                                    </div>
                                    <div className="mt-3 font-heading font-black text-base uppercase tracking-tight text-center group-hover:text-[#5D3FD3] transition-colors">
                                        {r.title}
                                    </div>
                                    <div className="mt-1 font-mono text-xs opacity-70">{cur}{Number(r.price || 0).toFixed(2)}</div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mt-16">
                    <Link to="/shop" className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-60 hover:opacity-100 transition-opacity">
                        ← {t("nav_shop") || "back to shop"}
                    </Link>
                </div>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.35em] opacity-60 mb-2">{title}</div>
            <p className="font-body text-sm leading-relaxed opacity-90 whitespace-pre-line">{children}</p>
        </div>
    );
}
